import { API } from '@pibex/shared';
import type { MeResponse, User } from '@pibex/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api.js';

export interface AuthState {
  user: User | null;
  loading: boolean;
  refresh(): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Resolves to the current user, or `null` when there is no usable session (401 or anything else). */
async function fetchCurrentUser(): Promise<User | null> {
  try {
    const { user } = await api.get<MeResponse>(API.me);
    return user;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const next = await fetchCurrentUser();
    setUser(next);
    setLoading(false);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post(API.authLogout);
    } finally {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchCurrentUser().then((next) => {
      if (cancelled) return;
      setUser(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, refresh, signOut }),
    [user, loading, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
